import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Page,
  Card,
  Layout,
  Filters,
  EmptyState,
  Button,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useIsPhone from "features/files/use-is-phone";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchBusinessTripsThunk,
  fetchMoreBusinessTrips,
  setFilterValue,
} from "./business-trips.slice";
import DataTable from "components/datatable/Datatable";
import PrettyDate from "components/format/PrettyDate";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import { getBusinessTripStatusTone } from "utils/businessTripStatus";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";

const END_REACHED_THRESHOLD = 200;

function BusinessTrips() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const isPhone = useIsPhone();

  const {
    collection,
    filtersValue,
    isLoading,
    isLoadingMore,
    isError,
    pagination,
  } = useSelector((state: RootState) => state.businessTripsReducer);

  const headerRef = useRef<HTMLDivElement>(null); // search/filters region (ResizeObserver)
  const listWrapRef = useRef<HTMLDivElement>(null); // wraps the table (measures top + hosts scroll listener)
  const sentinelRef = useRef<HTMLDivElement>(null); // infinite-scroll sentinel (phone)
  const [listMaxHeight, setListMaxHeight] = useState("calc(100vh - 280px)");

  const hasNextPage = Boolean(pagination?.hasNextPage);
  const nextPage = (pagination?.page || 1) + 1;

  // Initial load + reload page 1 whenever company or filters change.
  useEffect(() => {
    if (companyId) dispatch(fetchBusinessTripsThunk(companyId, filtersValue));
  }, [dispatch, filtersValue, companyId]);

  // Cap the table body height so only the trip rows scroll while the page
  // title, search bar and column headers stay fixed (uses DataTable's existing
  // maxBodyHeight, no component change).
  useLayoutEffect(() => {
    const compute = () => {
      const el = listWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = window.innerHeight - top - 24;
      setListMaxHeight(`${Math.max(Math.round(available), 240)}px`);
    };

    compute();
    window.addEventListener("resize", compute);

    let resizeObserver: ResizeObserver | undefined;
    if (headerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(compute);
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", compute);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const loadMore = () => {
    if (companyId && hasNextPage && !isLoading && !isLoadingMore) {
      dispatch(fetchMoreBusinessTrips(companyId, filtersValue, nextPage));
    }
  };

  // Desktop infinite scroll: listen to the DataTable's internal scroll area
  // (the element that also makes the header sticky).
  useEffect(() => {
    if (isPhone) return undefined;
    const wrap = listWrapRef.current;
    if (!wrap) return undefined;
    const scrollEl = wrap.querySelector<HTMLElement>(".overflow-y-auto");
    if (!scrollEl) return undefined;

    const onScroll = () => {
      if (isLoading || isLoadingMore || !hasNextPage) return;
      if (
        scrollEl.scrollTop + scrollEl.clientHeight >=
        scrollEl.scrollHeight - END_REACHED_THRESHOLD
      ) {
        loadMore();
      }
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // auto-fill if the first page doesn't fill the viewport
    return () => scrollEl.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    filtersValue,
    hasNextPage,
    isLoading,
    isLoadingMore,
    collection.length,
    isPhone,
  ]);

  // Phone infinite scroll: rows flow in the page, so observe a sentinel
  // against the viewport.
  useEffect(() => {
    if (!isPhone) return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filtersValue, hasNextPage, isLoading, isLoadingMore, isPhone]);

  const handleQueryChange = (value: string) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };
  const handleQueryValueRemove = () => {
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };
  const handleClearAll = () => {};
  const appliedFilters: any = [];
  if (filtersValue.textSearch && filtersValue.textSearch.length > 0) {
    appliedFilters.push({
      key: "textSearch",
      label: `${t("search")}: ${filtersValue.textSearch}`,
      onRemove: handleQueryValueRemove,
    });
  }

  const filters = [{ key: "", label: "", filter: <></> }];
  const filtersNode = (
    <Filters
      queryValue={filtersValue.textSearch}
      filters={filters}
      appliedFilters={appliedFilters}
      onQueryChange={handleQueryChange}
      onQueryClear={handleQueryValueRemove}
      onClearAll={handleClearAll}
      hideFilters
    />
  );

  const tableData =
    collection?.map((item: any, idx: number) => ({
      id: item.id,
      "#": idx + 1,
      dateFrom: item.dateFrom,
      dateTo: item.dateTo,
      user: typeof item.user === "object" ? item.user?.name : item.user,
      place: item.placeOfBusinessTrip || "-",
      purpose: item.purpose ? (String(item.purpose).length > 30 ? String(item.purpose).slice(0, 30) + "…" : item.purpose) : "-",
      advanceMoney: item.advanceMoney,
      substitute: typeof item.signatureUser === "object" ? item.signatureUser?.name : item.signatureUser || "-",
      status: t(`status_${item.status || "draft"}`),
      tone: getBusinessTripStatusTone(item.status),
    })) || [];

  const handleRowClick = (item: any) => {
    if (item.id && companyId) {
      navigate(`/app/${companyId}/business-trips/${item.id}`);
    }
  };

  // On a phone the whole card opens the trip (rowUrl below); on wider screens
  // the cell texts do.
  const openTrip = isPhone ? undefined : handleRowClick;

  const columns = [
    { key: "#" },
    {
      key: "dateFrom",
      name: t("date_from"),
      type: "clickabledate",
      onClick: openTrip,
    },
    {
      key: "dateTo",
      name: t("date_to"),
      type: "clickabledate",
      onClick: openTrip,
    },
    {
      key: "status",
      name: t("status"),
      type: "badge",
      onClick: openTrip,
      mobileView: "topRight",
    },
    {
      key: "user",
      name: t("user"),
      onClick: openTrip,
      mobileView: "bottomLeft",
    },
    {
      key: "place",
      name: t("place_of_business_trip"),
      onClick: openTrip,
      mobileView: "bottomRight",
    },
    { key: "purpose", name: t("purpose"), onClick: openTrip },
    {
      key: "advanceMoney",
      type: "currency",
      name: t("advance_for_expenses"),
      onClick: openTrip,
    },
    { key: "substitute", name: t("substitute_person"), onClick: openTrip },
  ] as Column[];

  // A phone card has four slots, so both dates share the top-left one.
  if (isPhone) {
    columns.push({
      key: "period",
      type: "custom",
      mobileView: "topLeft",
      render: (item: any) => (
        <span>
          <PrettyDate dateString={item.dateFrom} /> –{" "}
          <PrettyDate dateString={item.dateTo} />
        </span>
      ),
    });
  }

  const showSkeleton = isLoading && tableData.length === 0;
  const showEmpty = !isLoading && !isError && tableData.length === 0;

  return (
    <Layout>
      <Layout.Section>
        <Page
          fullWidth
          title={t("business_trips")}
          titleMetadata={
            <>
              <AuditLogPopover subjectType="BusinessTrip" />
              {isLoadingMore && (
                <Spinner
                  accessibilityLabel={t("loading", { defaultValue: "Loading" })}
                  size="small"
                />
              )}
            </>
          }
        >
          <Card>
            <div>
              <div ref={headerRef} style={{ padding: "16px", display: "flex" }}>
                <div style={{ flex: 1 }}>{filtersNode}</div>
              </div>
              <div ref={listWrapRef}>
                {showSkeleton ? (
                  <DataTableSkeleton rows={10} columns={columns.length} />
                ) : isError ? (
                  <EmptyState
                    heading={t("not_fetch", {
                      defaultValue: "Could not load business trips",
                    })}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <Button
                      onClick={() =>
                        companyId &&
                        dispatch(
                          fetchBusinessTripsThunk(companyId, filtersValue)
                        )
                      }
                    >
                      {t("retry", { defaultValue: "Retry" })}
                    </Button>
                  </EmptyState>
                ) : showEmpty ? (
                  <EmptyState
                    heading={t("no_data", {
                      defaultValue: "No business trips found",
                    })}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  />
                ) : (
                  <>
                    <DataTable
                      collection={tableData}
                      columns={columns}
                      rowUrl={
                        isPhone
                          ? (item) =>
                              `/app/${companyId}/business-trips/${item.id}`
                          : undefined
                      }
                      maxBodyHeight={isPhone ? undefined : listMaxHeight}
                    />
                    {/* Sentinel for phone infinite scroll (page-level scroll) */}
                    <div ref={sentinelRef} style={{ height: 1 }} />
                  </>
                )}
              </div>
            </div>
          </Card>
        </Page>
      </Layout.Section>
    </Layout>
  );
}

export default BusinessTrips;
